import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  heroArrowPath,
  heroBanknotes,
  heroBolt,
  heroBookOpen,
  heroChatBubbleLeftRight,
  heroCpuChip,
  heroEllipsisVertical,
  heroEye,
  heroFunnel,
  heroMagnifyingGlass,
  heroMegaphone,
  heroPencilSquare,
  heroPlus,
  heroTrash,
} from '@ng-icons/heroicons/outline';
import { NgpMenuTrigger } from 'ng-primitives/menu';
import { NgpSelectPortal } from 'ng-primitives/select';
import { catchError, forkJoin, map, of, switchMap } from 'rxjs';
import type { Agent } from '../../../core/api/generated/models/Agent';
import type { AgentArchetype } from '../../../core/api/generated/models/AgentArchetype';
import type { AgentStatus } from '../../../core/api/generated/models/AgentStatus';
import { HeaderSearchService } from '../../../core/search/header-search.service';
import {
  ToastrService,
  UiBadge,
  UiButton,
  UiConfirmDialogService,
  UiEmptyState,
  UiFilterActions,
  UiFilterBar,
  UiFilterField,
  UiFilterLabel,
  UiFilterSearch,
  UiInput,
  UiMenuItem,
  UiMenuPanel,
  UiMenuSeparator,
  UiPageHeader,
  UiResourceCard,
  UiResourceCardBody,
  UiResourceCardDescription,
  UiResourceCardFooter,
  UiResourceCardHeader,
  UiResourceCardMeta,
  UiResourceCardSkeleton,
  UiResourceCardTitle,
  UiResourceGrid,
  UiSelect,
  UiSelectDropdown,
  UiSelectOption,
} from '../../../shared/ui';
import { AgentsApiService } from '../agents-api.service';

const ALL_STATUSES = '__all__';
const ALL_OBJECTIVES = '__all__';
const CUSTOM_OBJECTIVE = '__custom__';

type AgentStatusFilter = typeof ALL_STATUSES | AgentStatus;
type AgentObjectiveFilter = typeof ALL_OBJECTIVES | typeof CUSTOM_OBJECTIVE | string;
type AgentBadgeTone = 'success' | 'warning' | 'neutral';

const AGENT_STATUS_LABELS: Record<AgentStatus, string> = {
  draft: 'Rascunho',
  active: 'Ativo',
  disabled: 'Desativado',
  archived: 'Arquivado',
};

const AGENT_STATUS_FILTER_OPTIONS = [
  { value: ALL_STATUSES, label: 'Todos os status' },
  { value: 'draft', label: AGENT_STATUS_LABELS.draft },
  { value: 'active', label: AGENT_STATUS_LABELS.active },
  { value: 'disabled', label: AGENT_STATUS_LABELS.disabled },
  { value: 'archived', label: AGENT_STATUS_LABELS.archived },
] satisfies readonly { value: AgentStatusFilter; label: string }[];

@Component({
  selector: 'app-agents-list-page',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIcon,
    NgpMenuTrigger,
    NgpSelectPortal,
    UiBadge,
    UiButton,
    UiEmptyState,
    UiFilterActions,
    UiFilterBar,
    UiFilterField,
    UiFilterLabel,
    UiFilterSearch,
    UiInput,
    UiMenuItem,
    UiMenuPanel,
    UiMenuSeparator,
    UiPageHeader,
    UiResourceCard,
    UiResourceCardBody,
    UiResourceCardDescription,
    UiResourceCardFooter,
    UiResourceCardHeader,
    UiResourceCardMeta,
    UiResourceCardSkeleton,
    UiResourceCardTitle,
    UiResourceGrid,
    UiSelect,
    UiSelectDropdown,
    UiSelectOption,
  ],
  providers: [
    provideIcons({
      heroArrowPath,
      heroBanknotes,
      heroBolt,
      heroBookOpen,
      heroChatBubbleLeftRight,
      heroCpuChip,
      heroEllipsisVertical,
      heroEye,
      heroFunnel,
      heroMagnifyingGlass,
      heroMegaphone,
      heroPencilSquare,
      heroPlus,
      heroTrash,
    }),
  ],
  templateUrl: './agents-list.page.html',
  styleUrl: './agents-list.page.css',
})
export class AgentsListPage {
  private readonly api = inject(AgentsApiService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly headerSearch = inject(HeaderSearchService);
  private readonly confirm = inject(UiConfirmDialogService);
  private readonly toast = inject(ToastrService);

  readonly loading = signal(false);
  readonly agents = signal<Agent[]>([]);
  readonly archetypes = signal<AgentArchetype[]>([]);
  readonly currentVersionNumbers = signal<Record<string, number | null>>({});
  readonly search = signal('');
  readonly statusFilter = signal<AgentStatusFilter>(ALL_STATUSES);
  readonly objectiveFilter = signal<AgentObjectiveFilter>(ALL_OBJECTIVES);
  readonly archivingAgentIds = signal<string[]>([]);
  readonly statusFilterOptions = AGENT_STATUS_FILTER_OPTIONS;

  readonly archetypeById = computed(() => new Map(this.archetypes().map((archetype) => [archetype.id, archetype])));

  readonly objectiveFilterOptions = computed(() => {
    const options: Array<{ value: AgentObjectiveFilter; label: string }> = [
      { value: ALL_OBJECTIVES, label: 'Todos os objetivos' },
      ...this.archetypes().map((archetype) => ({ value: archetype.id, label: archetype.name })),
    ];
    if (this.agents().some((agent) => !agent.archetypeId)) {
      options.push({ value: CUSTOM_OBJECTIVE, label: 'Sem objetivo padrão' });
    }
    return options;
  });

  readonly selectedStatusLabel = computed(
    () => this.statusFilterOptions.find((option) => option.value === this.statusFilter())?.label ?? 'Todos os status',
  );

  readonly selectedObjectiveLabel = computed(
    () => this.objectiveFilterOptions().find((option) => option.value === this.objectiveFilter())?.label ?? 'Todos os objetivos',
  );

  readonly hasActiveFilters = computed(
    () =>
      this.search().trim().length > 0
      || this.statusFilter() !== ALL_STATUSES
      || this.objectiveFilter() !== ALL_OBJECTIVES,
  );

  readonly visibleAgents = computed(() => {
    const query = normalizeSearchText(this.search());
    const status = this.statusFilter();
    const objective = this.objectiveFilter();
    const archetypes = this.archetypeById();

    return this.agents().filter((agent) => {
      if (status !== ALL_STATUSES && agent.status !== status) return false;
      if (objective === CUSTOM_OBJECTIVE && agent.archetypeId) return false;
      if (objective !== ALL_OBJECTIVES && objective !== CUSTOM_OBJECTIVE && agent.archetypeId !== objective) return false;
      if (!query) return true;

      const archetype = agent.archetypeId ? archetypes.get(agent.archetypeId) : null;
      return normalizeSearchText(
        [
          agent.name,
          agent.objective,
          agent.goal,
          agent.description,
          agent.status,
          AGENT_STATUS_LABELS[agent.status],
          archetype?.name,
          archetype?.description,
          agent.id,
        ].filter(Boolean).join(' '),
      ).includes(query);
    });
  });

  constructor() {
    this.headerSearch.register(
      {
        id: 'agents.search',
        label: 'Buscar agentes',
        placeholder: 'Buscar agentes',
        value: this.search,
        setValue: (value) => this.search.set(value),
        clear: () => this.search.set(''),
      },
      this.destroyRef,
    );
    this.refresh();
  }

  refresh(): void {
    this.loading.set(true);
    forkJoin({
      agents: this.api.listAgents(),
      archetypes: this.api.listArchetypes(),
    })
      .pipe(
        switchMap((response) =>
          this.loadCurrentVersionNumbers(response.agents.data).pipe(
            map((currentVersionNumbers) => ({ ...response, currentVersionNumbers })),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ agents, archetypes, currentVersionNumbers }) => {
          this.agents.set(agents.data);
          this.archetypes.set(archetypes.data);
          this.currentVersionNumbers.set(currentVersionNumbers);
          this.ensureObjectiveFilterIsValid();
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.toast.error('Não foi possível carregar os agentes.');
        },
      });
  }

  startNewAgent(): void {
    void this.router.navigate(['/agentes/novo']);
  }

  viewAgent(agent: Agent): void {
    void this.router.navigate(['/agentes', agent.id]);
  }

  editAgent(agent: Agent): void {
    void this.router.navigate(['/agentes', agent.id, 'editar']);
  }

  setSearchFromEvent(event: Event): void {
    const target = event.target;
    if (target instanceof HTMLInputElement) this.search.set(target.value);
  }

  setStatusFilter(value: unknown): void {
    if (this.statusFilterOptions.some((option) => option.value === value)) {
      this.statusFilter.set(value as AgentStatusFilter);
    }
  }

  setObjectiveFilter(value: unknown): void {
    if (typeof value !== 'string') return;
    if (this.objectiveFilterOptions().some((option) => option.value === value)) {
      this.objectiveFilter.set(value);
    }
  }

  clearFilters(): void {
    this.search.set('');
    this.statusFilter.set(ALL_STATUSES);
    this.objectiveFilter.set(ALL_OBJECTIVES);
  }

  async archiveAgent(agent: Agent): Promise<void> {
    if (this.archivingAgentIds().includes(agent.id)) return;
    const confirmed = await this.confirm.confirm({
      title: 'Excluir agente?',
      description: `${agent.name} será arquivado. Histórico, versões, execuções, feedback e memória continuam preservados.`,
      confirmLabel: 'Excluir',
      tone: 'danger',
    });
    if (!confirmed) return;

    this.archivingAgentIds.update((ids) => [...ids, agent.id]);
    this.api
      .archiveAgent(agent.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (archivedAgent) => {
          this.archivingAgentIds.update((ids) => ids.filter((id) => id !== archivedAgent.id));
          this.agents.update((items) => items.filter((item) => item.id !== archivedAgent.id));
          this.toast.success('Agente excluído', 'O agente foi arquivado e saiu da lista operacional.');
        },
        error: () => {
          this.archivingAgentIds.update((ids) => ids.filter((id) => id !== agent.id));
          this.toast.error('Não foi possível excluir o agente.');
        },
      });
  }

  isArchivingAgent(agentId: string): boolean {
    return this.archivingAgentIds().includes(agentId);
  }

  statusLabel(status: AgentStatus): string {
    return AGENT_STATUS_LABELS[status] ?? status;
  }

  statusTone(status: AgentStatus): AgentBadgeTone {
    if (status === 'active') return 'success';
    if (status === 'draft') return 'warning';
    return 'neutral';
  }

  agentDescription(agent: Agent): string {
    return agent.description || agent.objective || agent.goal || 'Sem descrição configurada.';
  }

  agentObjectiveLabel(agent: Agent): string {
    const archetype = agent.archetypeId ? this.archetypeById().get(agent.archetypeId) : null;
    return archetype?.name || agent.objective || agent.goal || 'Objetivo manual';
  }

  agentObjectiveIcon(agent: Agent): string {
    const archetype = agent.archetypeId ? this.archetypeById().get(agent.archetypeId) : null;
    const text = normalizeSearchText(`${archetype?.key ?? ''} ${archetype?.name ?? ''} ${agent.objective ?? ''} ${agent.goal ?? ''}`);
    if (text.includes('venda') || text.includes('receita') || text.includes('comercial')) return 'heroBanknotes';
    if (text.includes('atendimento') || text.includes('suporte') || text.includes('conversa')) return 'heroChatBubbleLeftRight';
    if (text.includes('marketing') || text.includes('campanha')) return 'heroMegaphone';
    if (text.includes('conhecimento') || text.includes('biblioteca')) return 'heroBookOpen';
    if (text.includes('automacao') || text.includes('operacao')) return 'heroBolt';
    if (text.includes('qualificacao') || text.includes('segmentacao')) return 'heroFunnel';
    return 'heroCpuChip';
  }

  currentVersionLabel(agent: Agent): string {
    if (!agent.currentVersionId) return 'Sem versão';
    const version = this.currentVersionNumbers()[agent.id];
    return typeof version === 'number' ? `v${version}` : 'Sem versão';
  }

  compactId(id: string): string {
    return `${id.slice(0, 8)}...${id.slice(-4)}`;
  }

  private loadCurrentVersionNumbers(agents: readonly Agent[]) {
    const loaders = agents.map((agent) => {
      if (!agent.currentVersionId) return of({ agentId: agent.id, version: null });
      return this.api.listVersions(agent.id).pipe(
        map((response) => ({
          agentId: agent.id,
          version: response.data.find((version) => version.id === agent.currentVersionId)?.version ?? null,
        })),
        catchError(() => of({ agentId: agent.id, version: null })),
      );
    });

    if (loaders.length === 0) return of({});
    return forkJoin(loaders).pipe(
      map((entries) =>
        entries.reduce<Record<string, number | null>>((acc, entry) => {
          acc[entry.agentId] = entry.version;
          return acc;
        }, {}),
      ),
    );
  }

  private ensureObjectiveFilterIsValid(): void {
    if (!this.objectiveFilterOptions().some((option) => option.value === this.objectiveFilter())) {
      this.objectiveFilter.set(ALL_OBJECTIVES);
    }
  }
}

function normalizeSearchText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR');
}
